/**
 * Project detail page.
 * Shows project information and documents.
 */

import { useState, useEffect, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { useProject, useDeleteProject, useUpdateProject } from "../hooks/useProjects";
import { useDocuments } from "../hooks/useDocuments";
import { useTags } from "../hooks/useTags";
import { ImportDocumentDialog } from "../components/import/ImportDocumentDialog";
import { useWorkspaceUrl } from "../lib/routes";
import { isProjectNameTaken } from "../lib/projectErrors";
import { KiiDialogLayout } from "../components/kii/kii-dialog-layout";
import { KiiEditableTitle } from "../components/kii/kii-editable-title";
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Clock,
  Users,
  ChevronRight,
  Check,
  X,
  Sparkles,
  EllipsisVertical,
  LayoutTemplate,
  Table2,
  Upload,
  FilePlus2,
  ScrollText,
  FileCode2,
  Mic,
  Image,
  Film,
  File,
} from "lucide-react";
import { useWorkspace } from "../contexts/WorkspaceContext";
import { createTag, setProjectTags, createDocument } from "../services/naraDoor";
import { useWorkspaceProjectTemplates, useProjectTemplate } from "../hooks/useProjectTemplates";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

export function ProjectDetail() {
  const { t } = useTranslation();
  const { formatDate } = useDateFormatter();
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const urls = useWorkspaceUrl();
  // Hooks now get workspaceId automatically from the store
  const { data: project, isLoading: projectLoading } = useProject(projectId);
  const { data: documentsData, isLoading: documentsLoading } = useDocuments(projectId);
  const deleteProject = useDeleteProject();
  const updateProject = useUpdateProject();

  const { activeWorkspace } = useWorkspace();
  // The server always assigns a template (T-370): the project's, else the workspace default, else the platform default.
  const effectiveTemplateId = project?.effective_template_id ?? project?.template_id;
  const { data: effectiveTemplate } = useProjectTemplate(effectiveTemplateId);
  const { data: workspaceTemplatesData } = useWorkspaceProjectTemplates(activeWorkspace?.id);
  const { tags: availableTags, isError: tagsError } = useTags();
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState("");
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);
  const [tagSaveFailed, setTagSaveFailed] = useState(false);
  const [createDocumentFailed, setCreateDocumentFailed] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);
  const tagContainerRef = useRef<HTMLDivElement>(null);

  const tagAccess = ((activeWorkspace?.settings?.tags) as any)?.access || "custom";

  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [editedInstructions, setEditedInstructions] = useState("");

  const documents = documentsData?.documents || [];
  const filteredDocuments = documents.filter((d) =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteProject = async () => {
    if (!projectId) return;
    try {
      await deleteProject.mutateAsync(projectId);
      setDeleteDialogOpen(false);
      if (urls) {
        navigate(urls.projects);
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const handleStartEditInstructions = () => {
    setEditedInstructions(project?.instructions || "");
    setIsEditingInstructions(true);
  };

  const handleCancelEditInstructions = () => {
    setIsEditingInstructions(false);
    setEditedInstructions("");
  };

  const handleSaveInstructions = async () => {
    if (!projectId) return;
    try {
      await updateProject.mutateAsync({
        projectId,
        data: { instructions: editedInstructions },
      });
      setIsEditingInstructions(false);
    } catch (error) {
      console.error("Failed to update instructions:", error);
    }
  };

  const handleSaveTemplate = async () => {
    if (!projectId) return;
    try {
      await updateProject.mutateAsync({
        projectId,
        data: { templateId: selectedTemplateId },
      });
      setTemplateDialogOpen(false);
    } catch (error) {
      console.error("Failed to update template:", error);
    }
  };

  const handleAddTag = async (tagId: string) => {
    if (!projectId) return;
    const currentIds = (project?.tags || []).map((t) => t.id);
    if (currentIds.includes(tagId)) return;
    try {
      await setProjectTags(projectId, [...currentIds, tagId], activeWorkspace?.id);
      setTagSaveFailed(false);
    } catch (error) {
      console.error("Failed to add tag:", error);
      setTagSaveFailed(true);
    }
    setTagInput("");
    setTagDropdownOpen(false);
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!projectId) return;
    const currentIds = (project?.tags || []).map((t) => t.id);
    try {
      await setProjectTags(projectId, currentIds.filter((id) => id !== tagId), activeWorkspace?.id);
      setTagSaveFailed(false);
    } catch (error) {
      console.error("Failed to remove tag:", error);
      setTagSaveFailed(true);
    }
  };

  const handleCreateAndAddTag = async () => {
    if (!projectId || !activeWorkspace?.id || !tagInput.trim()) return;
    try {
      const newTag = await createTag({
        workspaceId: activeWorkspace.id,
        name: tagInput.trim(),
      });
      // Add to project
      const currentIds = (project?.tags || []).map((t) => t.id);
      await setProjectTags(projectId, [...currentIds, newTag.id], activeWorkspace.id);
      setTagSaveFailed(false);
    } catch (error) {
      console.error("Failed to create tag:", error);
      setTagSaveFailed(true);
    }
    setTagInput("");
    setTagDropdownOpen(false);
  };

  // Filter suggestions: tags not already assigned, matching input
  const tagSuggestions = availableTags.filter((tag) => {
    const assigned = (project?.tags || []).map((t) => t.id);
    if (assigned.includes(tag.id)) return false;
    if (!tagInput) return true;
    return tag.name.toLowerCase().includes(tagInput.toLowerCase());
  });

  // Show "Create" option when input doesn't exactly match any existing tag
  const trimmedInput = tagInput.trim();
  const showCreateOption = trimmedInput.length > 0 &&
    !availableTags.some((t) => t.name.toLowerCase() === trimmedInput.toLowerCase());

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (tagContainerRef.current && !tagContainerRef.current.contains(e.target as Node)) {
        setTagDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDocTypeIcon = (docType?: string) => {
    switch (docType) {
      case "markdown": return FileCode2;
      case "recording": return Mic;
      case "audio": return Mic;
      case "photo": return Image;
      case "video": return Film;
      case "pdf": return File;
      default: return FileText;
    }
  };

  const getStatusIcon = (status?: string, docType?: string) => {
    const Icon = getDocTypeIcon(docType);
    switch (status) {
      case "ready":
      case "completed":
        return (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-nara-sage/15 flex items-center justify-center">
            <Icon className="h-5 w-5 text-nara-forest" />
          </div>
        );
      case "processing":
        return (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-nara-gold/15 flex items-center justify-center">
            <Icon className="h-5 w-5 text-nara-gold animate-pulse" />
          </div>
        );
      case "awaiting_boosts":
      case "insufficient_boosts":
        return (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-nara-gold/15 flex items-center justify-center">
            <Icon className="h-5 w-5 text-nara-gold" />
          </div>
        );
      case "failed":
        return (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
            <Icon className="h-5 w-5 text-red-500" />
          </div>
        );
      default:
        return (
          <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </div>
        );
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case "ready":
      case "completed":
        return (
          <span className="text-xs px-2 py-0.5 rounded-full bg-nara-sage/20 text-nara-charcoal dark:text-nara-sage font-medium">
            {t("projectDetail.statusReady", "Ready")}
          </span>
        );
      case "processing":
        return (
          <span className="text-xs px-2 py-0.5 rounded-full bg-nara-gold/15 text-nara-gold font-medium flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-nara-gold animate-pulse" />
            {t("projectDetail.statusProcessing", "Processing...")}
          </span>
        );
      case "failed":
        return (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-medium">
            {t("projectDetail.statusFailed", "Failed")}
          </span>
        );
      case "pending":
        return (
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
            {t("projectDetail.statusPending", "Pending")}
          </span>
        );
      default:
        return (
          <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
            {status || "pending"}
          </span>
        );
    }
  };

  const handleDocumentClick = (docId: string) => {
    if (urls && projectId) {
      navigate(urls.document(projectId, docId));
    }
  };

  if (projectLoading) {
    return (
      <div className="space-y-4">
        <div className="h-10 w-48 bg-muted rounded animate-pulse" />
        <div className="h-6 w-96 bg-muted rounded animate-pulse" />
        <div className="h-64 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="bento-card bento-surface border border-border text-center">
        <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="text-lg font-medium text-foreground mb-2">
          {t("projectDetail.notFound")}
        </h2>
        <p className="text-muted-foreground mb-4">
          {t("projectDetail.notFoundDescription")}
        </p>
        {urls && (
          <Link
            to={urls.projects}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors"
          >
            {t("projectDetail.backToProjects")}
          </Link>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs mb-5">
        {urls && (
          <Link
            to={urls.projects}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("nav.projects")}
          </Link>
        )}
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-foreground font-medium">{project.name}</span>
      </nav>

      {/* Hero Card */}
      <div className="bento-card bento-warm mb-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0 flex-1">
            <KiiEditableTitle
              value={project.name}
              onSave={(newName) => {
                if (!projectId) return;
                updateProject.mutate({ projectId, data: { name: newName } });
              }}
              className="text-2xl sm:text-3xl font-bold"
            />
            {isProjectNameTaken(updateProject.error) && (
              <p role="alert" className="mt-1 text-sm text-destructive">
                {t("projects.nameTaken")}
              </p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm bento-muted mt-2">
              {project.description && (
                <>
                  <span>{project.description}</span>
                  <span>&bull;</span>
                </>
              )}
              <span>
                {documents.length} {documents.length === 1 ? "document" : "documents"}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowCreateDialog(true)}
              className="kii-btn kii-btn-soft kii-btn-sm"
            >
              <Upload className="h-4 w-4" />
              {t("projectDetail.importDocument", "Import")}
            </button>
            <button
              onClick={async () => {
                if (!projectId || !urls) return;
                try {
                  const doc = await createDocument(projectId, {
                    name: t("markdownEditor.untitled", "Untitled Document"),
                    status: "ready",
                    contentMd: "",
                    documentType: "markdown",
                  });
                  navigate(urls.documentMarkdownEdit(projectId, doc.id));
                } catch (error) {
                  console.error("Failed to create document:", error);
                  setCreateDocumentFailed(true);
                }
              }}
              className="kii-btn kii-btn-soft kii-btn-sm"
            >
              <FilePlus2 className="h-4 w-4" />
              {t("projectDetail.createDocument", "Create")}
            </button>
            {createDocumentFailed && (
              <span role="alert" className="text-sm text-destructive">
                {t("projectDetail.createDocumentError")}
              </span>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="p-2 text-muted-foreground hover:bg-accent rounded-lg transition-colors"
                  aria-label={t("common.more", "More")}
                >
                  <EllipsisVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setEditedInstructions(project?.instructions || "");
                    setIsEditingInstructions(true);
                    setInstructionsDialogOpen(true);
                  }}
                >
                  <ScrollText className="h-4 w-4" />
                  {t("projectDetail.instructions")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    setSelectedTemplateId(effectiveTemplateId ?? null);
                    setTemplateDialogOpen(true);
                  }}
                >
                  <LayoutTemplate className="h-4 w-4" />
                  {t("projectDetail.changeTemplate")}
                </DropdownMenuItem>
                {urls && projectId && (
                  <DropdownMenuItem
                    onClick={() => navigate(urls.projectCollaborators(projectId))}
                  >
                    <Users className="h-4 w-4" />
                    {t("projectDetail.collaborators")}
                  </DropdownMenuItem>
                )}
                {urls && projectId && effectiveTemplate?.default_extraction_recipe_id && (
                  <DropdownMenuItem
                    onClick={() => navigate(urls.projectCatalog(projectId))}
                  >
                    <Table2 className="h-4 w-4" />
                    {t("projectDetail.productCatalog")}
                  </DropdownMenuItem>
                )}
                {urls && projectId && (
                  <DropdownMenuItem
                    onClick={() => navigate(urls.projectGenerate(projectId))}
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("projectDetail.generateDocument")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDeleteDialogOpen(true)}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950"
                >
                  <Trash2 className="h-4 w-4" />
                  {t("projectDetail.deleteProject")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Project Tags — inline typeahead */}
      {tagAccess !== "none" && (
        <div className="mb-5 flex flex-wrap items-center gap-2" ref={tagContainerRef}>
          {/* Assigned tag badges */}
          {(project?.tags || []).map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-sm font-medium bg-muted text-foreground"
            >
              {tag.color && (
                <span
                  className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
              )}
              {tag.name}
              <button
                onClick={() => handleRemoveTag(tag.id)}
                className="ml-0.5 p-0.5 rounded-full hover:bg-foreground/10 transition-colors"
                aria-label={`Remove ${tag.name}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}

          {tagsError && <span className="text-sm text-red-600">{t("projectDetail.tagsLoadError")}</span>}
          {tagSaveFailed && (
            <span role="alert" className="text-sm text-destructive">
              {t("projectDetail.tagSaveError")}
            </span>
          )}

          {/* Typeahead input */}
          <div className="relative">
            <input
              ref={tagInputRef}
              type="text"
              value={tagInput}
              onChange={(e) => {
                setTagInput(e.target.value);
                setTagDropdownOpen(true);
              }}
              onFocus={() => setTagDropdownOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setTagDropdownOpen(false);
                  tagInputRef.current?.blur();
                }
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (showCreateOption) {
                    handleCreateAndAddTag();
                  } else if (tagSuggestions.length > 0) {
                    handleAddTag(tagSuggestions[0].id);
                  }
                }
              }}
              placeholder={project?.tags?.length ? "Add tag..." : "Type to add tags..."}
              className="w-36 px-2.5 py-1 text-sm border border-dashed border-border rounded-full bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
            />

            {/* Suggestions dropdown */}
            {tagDropdownOpen && (tagSuggestions.length > 0 || showCreateOption) && (
              <div className="absolute z-50 top-full left-0 mt-1 w-56 max-h-48 overflow-y-auto bg-popover border border-border rounded-lg shadow-lg">
                {tagSuggestions.map((tag) => (
                  <button
                    key={tag.id}
                    onClick={() => handleAddTag(tag.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors first:rounded-t-lg last:rounded-b-lg"
                  >
                    {tag.color && (
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                    )}
                    <span className="truncate">{tag.name}</span>
                  </button>
                ))}
                {showCreateOption && (
                  <button
                    onClick={handleCreateAndAddTag}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-accent transition-colors border-t border-border text-primary font-medium"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Create &ldquo;{trimmedInput}&rdquo;
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Documents Section */}
      <div className="mb-5">
        <h2 className="text-xl font-semibold mb-4">{t("projectDetail.documents")}</h2>

        {/* Search */}
        <div className="relative mb-5">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <input
            type="text"
            placeholder={t("projectDetail.searchDocuments")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="kii-input pl-11 w-full"
          />
        </div>

        {/* Documents Grid */}
        {documentsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-36 bg-muted rounded-[1.25rem] animate-pulse" />
            ))}
          </div>
        ) : filteredDocuments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {filteredDocuments.map((doc) => (
              <div
                key={doc.id}
                className="bento-card bento-surface bento-interactive border border-border"
                onClick={() => handleDocumentClick(doc.id)}
              >
                <div className="flex items-start gap-4">
                  {getStatusIcon(doc.status, doc.document_type)}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-foreground truncate">
                        {doc.name}
                      </h3>
                      {getStatusBadge(doc.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {doc.document_type || doc.file_type || t("projectDetail.document")}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground mt-4 pt-3 border-t border-border">
                  <Clock className="h-3.5 w-3.5" />
                  {formatDate(doc.updated_at, "long-datetime")}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bento-card bento-surface border border-border text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-medium text-foreground mb-2">
              {searchTerm ? t("projectDetail.noMatchingDocuments") : t("projectDetail.noDocuments")}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? t("projectDetail.adjustSearch")
                : t("projectDetail.createFirstDocument")}
            </p>
            {!searchTerm && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => setShowCreateDialog(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  {t("projectDetail.importDocument", "Import")}
                </button>
                <button
                  onClick={async () => {
                    if (!projectId || !urls) return;
                    try {
                      const doc = await createDocument(projectId, {
                        name: t("markdownEditor.untitled", "Untitled Document"),
                        status: "ready",
                        contentMd: "",
                      });
                      navigate(urls.documentMarkdownEdit(projectId, doc.id));
                    } catch (error) {
                      console.error("Failed to create document:", error);
                      setCreateDocumentFailed(true);
                    }
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors"
                >
                  <FilePlus2 className="h-4 w-4" />
                  {t("projectDetail.createDocument", "Create")}
                </button>
                {createDocumentFailed && (
                  <span role="alert" className="text-sm text-destructive">
                    {t("projectDetail.createDocumentError")}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Import Document Dialog */}
      <ImportDocumentDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        projectId={projectId!}
      />

      {/* Project Instructions Dialog */}
      <KiiDialogLayout
        open={instructionsDialogOpen}
        onOpenChange={(open) => {
          setInstructionsDialogOpen(open);
          if (!open) {
            setIsEditingInstructions(false);
            setEditedInstructions("");
          }
        }}
        title={t("projectDetail.instructions")}
        size="md"
        footer={
          isEditingInstructions ? (
            <>
              <button
                type="button"
                onClick={() => {
                  handleCancelEditInstructions();
                  if (!project?.instructions) {
                    setInstructionsDialogOpen(false);
                  }
                }}
                disabled={updateProject.isPending}
                className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={async () => {
                  await handleSaveInstructions();
                  setInstructionsDialogOpen(false);
                }}
                disabled={updateProject.isPending}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
              >
                {updateProject.isPending ? t("common.saving") : t("common.save")}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => setInstructionsDialogOpen(false)}
              className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors"
            >
              {t("common.close", "Close")}
            </button>
          )
        }
      >
        <div className="mt-4">
          <p className="text-sm text-muted-foreground mb-3">
            {t("projectDetail.instructionsDescription", "Add instructions for how Nara Recorder users should work on this project.")}
          </p>
          {isEditingInstructions ? (
            <textarea
              value={editedInstructions}
              onChange={(e) => setEditedInstructions(e.target.value)}
              placeholder={t("projectDetail.instructionsPlaceholder")}
              className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              rows={6}
              autoFocus
            />
          ) : (
            <div>
              <p className={`text-sm whitespace-pre-wrap ${project?.instructions ? "text-foreground" : "text-muted-foreground italic"}`}>
                {project?.instructions || t("projectDetail.noInstructions")}
              </p>
              <button
                onClick={handleStartEditInstructions}
                className="mt-3 text-sm text-primary hover:underline"
              >
                {t("common.edit")}
              </button>
            </div>
          )}
        </div>
      </KiiDialogLayout>

      {/* Delete Project Confirmation Dialog */}
      <KiiDialogLayout
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={t("projectDetail.deleteProjectTitle")}
        description={t("projectDetail.deleteProjectDescription")}
        size="sm"
        footer={
          <>
            <button
              type="button"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleteProject.isPending}
              className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleDeleteProject}
              disabled={deleteProject.isPending}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {deleteProject.isPending ? t("projectDetail.deleting") : t("projectDetail.deleteProjectTitle")}
            </button>
          </>
        }
      >
        <div className="mt-6 p-4 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t("projectDetail.warning")}</strong> {t("projectDetail.aboutToDelete", { name: project?.name })}
          </p>
        </div>
      </KiiDialogLayout>

      {/* Change Template Dialog */}
      <KiiDialogLayout
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        title={t("projectDetail.changeTemplate")}
        description={t("projectDetail.templateDescription")}
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setTemplateDialogOpen(false)}
              disabled={updateProject.isPending}
              className="px-4 py-2 text-foreground hover:bg-accent rounded-lg transition-colors disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={updateProject.isPending}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {updateProject.isPending ? t("common.loading") : t("common.save")}
            </button>
          </>
        }
      >
        <div className="mt-4 space-y-2">
          {/* Available templates */}
          {workspaceTemplatesData?.templates?.map((tmpl) => (
            <label
              key={tmpl.id}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                selectedTemplateId === tmpl.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:bg-accent"
              }`}
            >
              <input
                type="radio"
                name="template"
                checked={selectedTemplateId === tmpl.id}
                onChange={() => setSelectedTemplateId(tmpl.id)}
                className="mt-1"
              />
              <div>
                <p className="text-sm font-medium text-foreground">{tmpl.name}</p>
                {tmpl.description && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {tmpl.description}
                  </p>
                )}
                {tmpl.document_recipe_ids && tmpl.document_recipe_ids.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {tmpl.document_recipe_ids.length} {tmpl.document_recipe_ids.length === 1 ? "document recipe" : "document recipes"}
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>
      </KiiDialogLayout>

    </div>
  );
}
